import { useCallback, useState } from 'react';
import { type LayoutChangeEvent, View } from 'react-native';
import { colors } from '../../theme/colors';
import { DonutChart, LineChart } from './index';
import type { ColoredDataPoint, DataPoint } from './types';

// FLOSEAI-438: shared responsive chart wrappers. Both the dashboard panel and
// the analytics overview render Leads-Over-Time / Lead-Sources charts that must
// fill their container instead of a capped fixed width, so the sizing lives in
// one place (Scope Rule: used by 2+ features -> shared).

// AC4: heights track the measured width but stay within these bounds so data
// and labels are never clipped.
const MIN_CHART_HEIGHT = 220;
const MAX_CHART_HEIGHT = 300;
const LINE_HEIGHT_RATIO = 0.34;
const MIN_DONUT_SIZE = 160;
// The donut is one reading in a card, not the card's subject. Letting the
// diameter track the full card width blew it up to ~380px on a wide screen for
// a single-slice series, which forced the whole chart row taller and left the
// line-chart card beside it mostly empty.
const DONUT_WIDTH_RATIO = 0.5;
const MAX_DONUT_SIZE = 240;
// The stacked legend sits below the donut; we reserve room for it so the donut
// plus its legend never exceeds MAX_CHART_HEIGHT (AC4).
const DONUT_LEGEND_TOP_MARGIN = 12;
const DONUT_LEGEND_ROW_HEIGHT = 28;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// AC4: line height tracks width but stays within the 240–420 bound.
export function computeLineHeight(width: number): number {
  return clamp(Math.round(width * LINE_HEIGHT_RATIO), MIN_CHART_HEIGHT, MAX_CHART_HEIGHT);
}

function donutLegendHeight(sourceCount: number): number {
  return DONUT_LEGEND_TOP_MARGIN + Math.max(0, sourceCount) * DONUT_LEGEND_ROW_HEIGHT;
}

// The donut grows to fill its card width (AC1) rather than freezing at a fixed
// cap. Its diameter is bounded by the AC4 height budget minus the stacked
// legend, so the donut plus legend never exceeds MAX_CHART_HEIGHT (nothing clips).
export function computeDonutSize(width: number, sourceCount: number): number {
  const heightBudget = MAX_CHART_HEIGHT - donutLegendHeight(sourceCount);
  const maxDiameter = Math.max(MIN_DONUT_SIZE, Math.min(MAX_DONUT_SIZE, heightBudget));
  return clamp(Math.round(width * DONUT_WIDTH_RATIO), MIN_DONUT_SIZE, maxDiameter);
}

export function donutTotalHeight(width: number, sourceCount: number): number {
  return computeDonutSize(width, sourceCount) + donutLegendHeight(sourceCount);
}

// Measures the rendered width of its host so children can size relative to the
// real available space (AC1/AC5) rather than a precomputed window estimate.
export function useMeasuredWidth() {
  const [width, setWidth] = useState(0);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setWidth(e.nativeEvent.layout.width);
  }, []);
  return { width, onLayout };
}

interface ResponsiveLineChartProps {
  data: DataPoint[];
  color?: string;
  showArea?: boolean;
  showGrid?: boolean;
  showLabels?: boolean;
  showYAxisLabels?: boolean;
  showDots?: boolean;
}

// Fills its container width (LineChart self-measures when no width prop is
// passed) and derives a clamped, proportional height (AC4).
export function ResponsiveLineChart({
  data,
  color = colors.primary.neon,
  showArea = true,
  showGrid = true,
  showLabels = true,
  showYAxisLabels = true,
  showDots = false,
}: Readonly<ResponsiveLineChartProps>) {
  const { width, onLayout } = useMeasuredWidth();
  const height = computeLineHeight(width);

  return (
    <View style={{ flex: 1, minHeight: MIN_CHART_HEIGHT }} onLayout={onLayout}>
      <LineChart
        data={data}
        height={height}
        color={color}
        showArea={showArea}
        showGrid={showGrid}
        showLabels={showLabels}
        showYAxisLabels={showYAxisLabels}
        showDots={showDots}
      />
    </View>
  );
}

interface ResponsiveDonutChartProps {
  data: ColoredDataPoint[];
  total: number;
  strokeWidth?: number;
  centerLabel?: string;
}

// Scales the donut diameter to the available width within sensible bounds so it
// grows with the card instead of staying pinned at a small fixed size (AC1).
export function ResponsiveDonutChart({
  data,
  total,
  strokeWidth = 20,
  centerLabel = 'Total',
}: Readonly<ResponsiveDonutChartProps>) {
  const { width, onLayout } = useMeasuredWidth();
  const size = computeDonutSize(width, data.length);

  return (
    <View
      style={{
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: MIN_CHART_HEIGHT,
      }}
      onLayout={onLayout}
    >
      <DonutChart
        data={data}
        size={size}
        strokeWidth={strokeWidth}
        showLegend
        centerValue={total.toString()}
        centerLabel={centerLabel}
      />
    </View>
  );
}
